// @file Vue composable for all navigation related logic in Slideshow

import { trackEvent } from '@@/bits/analytics'
import { isAppUsing } from '@@/bits/flip'
import { toggleFullscreen } from '@@/bits/fullscreen'
import { SLIDESHOW_ANALYTICS_ID } from '@@/bits/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSlideshowOverlays } from '@@/vuecomposables/slideshow_overlays'
import { useSlideshowPages } from '@@/vuecomposables/slideshow_pages'
import { useAccessibilitySettings } from '@@/vuecomposables/useAccessibilitySettings'
import { storeToRefs } from 'pinia'
import type { KeyBindingMap } from 'tinykeys'
import { createKeybindingsHandler } from 'tinykeys'
import type { Ref } from 'vue'
import { nextTick, onMounted, onUnmounted, watch } from 'vue'

export const useSlideshowNavigation = ({
  currentPageElement,
  slideshowNavigationBarContainerRef,
}: {
  currentPageElement: HTMLDivElement | undefined
  slideshowNavigationBarContainerRef: Ref<HTMLDivElement | undefined>
}): void => {
  const surfaceStore = useSurfaceStore()
  const { dir, user } = storeToRefs(surfaceStore)

  const { goToPage, prevPageIndex, nextPageIndex, numberOfPages } = useSlideshowPages()
  const { isAnyOverlayOpen } = useSlideshowOverlays()

  const { isKeyboardShortcutsEnabled } = useAccessibilitySettings(user)

  // We call `focusCurrentPage` outside `goToNextPage` and `goToPreviousPage`
  // because we only want to invoke it when navigating with the keys
  // and not every time the page changes.
  const focusCurrentPage = (): void => {
    nextTick(() => {
      if (currentPageElement == null) return
      currentPageElement.focus()
    })
  }

  const onForwardShortcutKey = (keyboardTarget: string): void => {
    if (dir.value === 'rtl') {
      if (prevPageIndex.value == null) return
      goToPage(prevPageIndex.value, keyboardTarget)
    } else {
      if (nextPageIndex.value == null) return
      goToPage(nextPageIndex.value, keyboardTarget)
    }
    focusCurrentPage()
  }

  const onBackwardShortcutKey = (keyboardTarget: string): void => {
    if (dir.value === 'rtl') {
      if (nextPageIndex.value == null) return
      goToPage(nextPageIndex.value, keyboardTarget)
    } else {
      if (prevPageIndex.value == null) return
      goToPage(prevPageIndex.value, keyboardTarget)
    }
    focusCurrentPage()
  }

  const onFirstPageShortcutKey = (keyboardTarget: string): void => {
    goToPage(0, keyboardTarget)
    focusCurrentPage()
  }

  const onLastPageShortcutKey = (keyboardTarget: string): void => {
    goToPage(numberOfPages.value - 1, keyboardTarget)
    focusCurrentPage()
  }

  const setupShortcuts = (): KeyBindingMap => {
    const shortcuts: KeyBindingMap = {
      ArrowLeft: () => onBackwardShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-left-arrow-key']),
      ArrowUp: () => onBackwardShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-up-arrow-key']),
      PageUp: () => onBackwardShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-page-up-key']),
      PageDown: () => onForwardShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-page-down-key']),
      ArrowRight: () => onForwardShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-right-arrow-key']),
      ArrowDown: () => onForwardShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-down-arrow-key']),
      Space: () => {
        if (slideshowNavigationBarContainerRef.value?.contains(document.activeElement) === true) {
          return
        }
        onForwardShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-space-key'])
      },
      Home: () => onFirstPageShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-home-key']),
      End: () => onLastPageShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-end-key']),
    }

    if (!isAppUsing('keyboardShortcutsSetting') || isKeyboardShortcutsEnabled.value) {
      shortcuts.F = () => {
        trackEvent('keyboard', {
          target: SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-f-key'],
        })
        toggleFullscreen()
      }
      shortcuts.R = () => onFirstPageShortcutKey(SLIDESHOW_ANALYTICS_ID['slideshow-shortcut-r-key'])
    }

    return shortcuts
  }

  let keyboardShortcutsHandler = createKeybindingsHandler(setupShortcuts())

  const startListeningForKeyboardShortcuts = (): void => window.addEventListener('keydown', keyboardShortcutsHandler)
  const stopListeningForKeyboardShortcuts = (): void => window.removeEventListener('keydown', keyboardShortcutsHandler)

  onMounted(() => {
    startListeningForKeyboardShortcuts()
  })

  onUnmounted(() => {
    stopListeningForKeyboardShortcuts()
  })

  watch(isAnyOverlayOpen, () => {
    if (isAnyOverlayOpen.value) {
      stopListeningForKeyboardShortcuts()
    } else {
      startListeningForKeyboardShortcuts()
    }
  })

  watch(isKeyboardShortcutsEnabled, () => {
    stopListeningForKeyboardShortcuts()
    keyboardShortcutsHandler = createKeybindingsHandler(setupShortcuts())
    if (!isAnyOverlayOpen.value) {
      startListeningForKeyboardShortcuts()
    }
  })
}
